import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Shield, CheckCircle2, AlertTriangle, Eye, EyeOff, Loader2, Lock, Fingerprint, ShieldX, LogOut, ShieldCheck, HelpCircle, Smartphone, Download, ArrowUp, PlusSquare, ShieldAlert, Send } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { Modal } from '../../components/ui/Modal';
import { StyledQrCode } from '../../security/qr/StyledQrCode';
import { LabPublicBar } from '../../lab/LabPublicBar';

import { getWizardRoute } from '../../config/wizard-map';
import { AcessoDiretoFlow } from './AcessoDiretoFlow';
import { trackEvent } from '../../api/observe';
import { clearPasskeyOnThisDevice, markPasskeyOnThisDevice } from '../../auth/passkeyDevice';
import { getRedirectPath } from '../../auth/routes';
import { saveAccountToLocal } from '../../auth/savedAccounts';
import { usePasskeyEnrollment } from '../../hooks/usePasskeyEnrollment';
import { AcShieldLogo } from '../../components/security/AcShieldLogo';
import { DeviceRegistrationDialog } from '../../components/security/DeviceRegistrationDialog';
import {
  getPasskeyStatus,
  getPublicPasskeyConfig,
  requestPasskeyAuthOptions,
  verifyPasskeyAuthentication,
} from '../../security/passkey/passkeyApi';
import {
  activateByToken,
  activateWithPassword,
  getActivationQuizQuestions,
  loginAfterActivation,
  verifyActivationQuiz,
  verifyActivationToken,
} from '../../security/activationApi';
import '../../components/ui/datatable.css';

// ─── Sovereign Dark Styles ──────────────────────────────────────────
const SOVEREIGN_BG: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'linear-gradient(-45deg, #0f172a, #1e1b4b, #020617, #172554)',
  backgroundSize: '400% 400%',
  animation: 'pan-bg 15s ease infinite',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  padding: '12px 10px', position: 'relative' as any,
};

const SOVEREIGN_CARD: React.CSSProperties = {
  background: '#ffffff', borderRadius: 22, padding: 'clamp(20px, 4vw, 36px)',
  maxWidth: 520, width: '100%', position: 'relative' as any, zIndex: 1,
  boxShadow: '0 25px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
  textAlign: 'center' as any,
  animation: 'fade-in-up 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Steps com pergunta de tipo de dispositivo + passkey (dispositivo privado)
const STEPS_DEFAULT = ['Verificando', 'Criar Senha', 'Dispositivo', 'Chave de Acesso', 'Ativada'];
const STEPS_QUIZ = ['Verificando', 'Identidade', 'Criar Senha', 'Dispositivo', 'Chave de Acesso', 'Ativada'];

interface UserInfo {
  id: string;
  nome: string;
  email: string;
  perfil: string;
  cargo?: string | null;
  escola?: { nome: string; cidade: string; uf: string } | null;
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return '***';
  const maskedLocal = local.length <= 2 ? local[0] + '***' : local.slice(0, 2) + '***';
  const parts = domain.split('.');
  const maskedDomain = parts[0].length <= 2 ? parts[0] + '***' : parts[0].slice(0, 2) + '***';
  return `${maskedLocal}@${maskedDomain}.${parts.slice(1).join('.')}`;
}

const perfilLabel: Record<string, string> = {
  ADMIN: 'Administrador',
  REDE: 'Gestor de Rede',
  ESTADUAL: 'Gestor Estadual',
  MUNICIPAL: 'Gestor Municipal',
  ESCOLA: 'Equipe da Escola',
  PROFESSOR: 'Professor',
  RESPONSAVEL: 'Responsavel',
  PUBLICO: 'Visitante',
};

// ─── Componente ──────────────────────────────────────────────────────────────

//#region AC_BLOCK ativar-page.ativar-page -- Ativacao de conta e passkey
// @ac-purpose Controla ativacao por token/convite, senha inicial, dispositivo privado/publico e cadastro/verificacao de passkey.
// @ac-related react-app/src/App.tsx#AtivarPage
// @ac-related react-app/src/security/activationApi.ts
// @ac-related react-app/src/security/passkey/passkeyApi.ts
// @ac-routes /ativar, /ativar/:token/:code?, /convite
// @ac-tests node tools/codex-kit.mjs check react-tsc
export function AtivarPage() {
  // Render counter to detect unexpected re‑renders (debug)
  const renderCount = useRef(0);
  renderCount.current += 1;
  if (renderCount.current > 10) {
    console.warn('[AtivarPage] Excessive renders detected:', renderCount.current);
  }
  console.log('[AtivarPage] ✅ Componente montou!', { href: window.location.href });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Ao entrar na tela de ativação, limpa TODA sessão antiga para garantir
    // que beta-testers não herdem cache stale de escolas/wizards anteriores.
    try {
      // 1. Limpar dados de escola/cargo
      localStorage.removeItem('ac_escolaId');
      localStorage.removeItem('ac_nomeEscola');
      localStorage.removeItem('ac_cargo');
      sessionStorage.removeItem('ac_escolaId');
      sessionStorage.removeItem('ac_nomeEscola');
      sessionStorage.removeItem('ac_cargo');

      // 2. Limpar TODOS os caches de wizard concluído (ac_wizard_concluido_*)
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('ac_wizard_concluido_') || key?.startsWith('ac_wizard_grade_vista_')) keysToRemove.push(key);
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));

      // 3. Limpar dados de auth antigos
      localStorage.removeItem('ac_user');
      localStorage.removeItem('ac_token');
      sessionStorage.removeItem('ac_user');
      sessionStorage.removeItem('ac_token');
    } catch {
      // ignore — storage pode estar bloqueado em modo privado
    }
  }, []);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const params = useParams();
  const resolveParam = (name: string) => {
    const direct = searchParams.get(name);
    if (direct) return direct;

    const hash = window.location.hash?.replace(/^#/, '') || '';
    if (hash) {
      const hashParams = new URLSearchParams(hash.startsWith('?') ? hash.slice(1) : hash);
      const fromHash = hashParams.get(name);
      if (fromHash) return fromHash;
    }

    const href = window.location.href || '';
    const regex = new RegExp(`[?&#]${name}=([^&#]+)`, 'i');
    const match = href.match(regex);
    if (match?.[1]) return decodeURIComponent(match[1]);

    return '';
  };

  const token =
    params.token ||
    resolveParam('token') ||
    resolveParam('Token') ||
    resolveParam('t') ||
    sessionStorage.getItem('ac_activation_token') ||
    '';
  const inviteCode =
    params.code ||
    resolveParam('code') ||
    resolveParam('c') ||
    sessionStorage.getItem('ac_activation_code') ||
    '';
  // Segurança de fluxo: se houver token no link, priorizar ativação por token e ignorar fromLogin.
  const fromLogin = !token && searchParams.get('fromLogin') === '1';
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    const urlToken = params.token || resolveParam('token') || resolveParam('Token') || resolveParam('t');
    const urlCode = params.code || resolveParam('code') || resolveParam('c');

    if (urlToken) {
      sessionStorage.setItem('ac_activation_token', urlToken);
      console.log('[AtivarPage] Salvo token de ativação no sessionStorage:', urlToken.slice(0, 8) + '...');
    }
    if (urlCode) {
      sessionStorage.setItem('ac_activation_code', urlCode);
    }
  }, [params.token, params.code, location.search]);

  const [step, setStep] = useState(0);
  const [showAlreadyLoggedInDialog, setShowAlreadyLoggedInDialog] = useState(false);
  const [checkingSession, setCheckingSession] = useState(isAuthenticated && !fromLogin);
  const { registerPasskey, error: passkeyHookError, loading: passkeyHookLoading } = usePasskeyEnrollment();
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
  const isStandalone = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(() => {
    try {
      const urlToken = new URLSearchParams(window.location.search).get('token') || sessionStorage.getItem('ac_activation_token');
      if (urlToken) {
        const cached = localStorage.getItem('ac_user_info_' + urlToken);
        if (cached) return JSON.parse(cached);
      }
    } catch {}
    return null;
  });
  const [wizardRoute, setWizardRoute] = useState<string | null>(null);
  const [acessoDireto, setAcessoDireto] = useState(false);
  const [isResponsavel, setIsResponsavel] = useState(false);
  const [hasDivergentData, setHasDivergentData] = useState(false);

  // Quiz de verificação de identidade (Responsável)
  const [quizPerguntas, setQuizPerguntas] = useState<any[]>([]);
  const [quizStep, setQuizStep] = useState(0); // 0-4
  const [quizRespostas, setQuizRespostas] = useState<Record<number, string>>({});
  const [quizSelecionada, setQuizSelecionada] = useState<string | null>(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizBloqueado, setQuizBloqueado] = useState(false);
  const [quizMensagem, setQuizMensagem] = useState('');
  const [quizNomeAluno, setQuizNomeAluno] = useState('');



  // Fluxo com senha (fallback)
  const [senha, setSenha] = useState('');
  const [senhaConfirm, setSenhaConfirm] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReenvioDialog, setShowReenvioDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // ── Passkey Challenge (Fase A) ─────────────────────────────────────
  const [passkeyPhase, setPasskeyPhase] = useState<'idle' | 'checking' | 'challenging' | 'success' | 'failed'>('idle');
  const [passkeyAttempts, setPasskeyAttempts] = useState(0);
  const [passkeyError, setPasskeyError] = useState('');
  const passkeyJwtRef = useRef(''); // JWT temporário até Passkey ser verificada
  const passkeyWizardRef = useRef(''); // rota de destino
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [fromLoginNeedsRegister, setFromLoginNeedsRegister] = useState(false);
  const [isPasskeyEnabled, setIsPasskeyEnabled] = useState(true);
  const [showDeviceReg, setShowDeviceReg] = useState(false);
  const [deviceSomenteMeu, setDeviceSomenteMeu] = useState<boolean | null>(null);

  // STEPS dinâmico baseado no perfil
  const STEPS = isResponsavel ? STEPS_QUIZ : STEPS_DEFAULT;

  // Mapeamento de steps:
  // Default: 0=verificando, 1=senha, 2=dispositivo, 3=passkey, 4=ativada
  // Quiz:    0=verificando, 1=identidade, 2=senha, 3=dispositivo, 4=passkey, 5=ativada
  const PASSWORD_STEP = isResponsavel ? 2 : 1;
  const DEVICE_STEP = PASSWORD_STEP + 1;
  const PASSKEY_STEP = DEVICE_STEP + 1;
  const DONE_STEP = PASSKEY_STEP + 1;

  // Fetch passkey configuration on mount
  useEffect(() => {
    getPublicPasskeyConfig()
      .then(data => {
        if (data && typeof data.ativo === 'boolean') {
          setIsPasskeyEnabled(data.ativo);
        }
      })
      .catch(() => {});
  }, []);

  // Auto PWA redirect desativado na ativação para evitar falhas em webviews/links de WhatsApp
  useEffect(() => {
    console.log('[PWA] Auto-redirecionamento desativado na tela de ativação por razões de compatibilidade.');
  }, []);

;

  // ── Handler: avançar quiz ──
  const handleQuizNext = useCallback(async () => {
    if (!quizSelecionada) return;
    const perguntaAtual = quizPerguntas[quizStep];
    const novasRespostas = { ...quizRespostas, [perguntaAtual.id]: quizSelecionada };
    setQuizRespostas(novasRespostas);
    setQuizSelecionada(null);

    if (quizStep < quizPerguntas.length - 1) {
      // Próxima pergunta
      setQuizStep(quizStep + 1);
    } else {
      // Última pergunta → enviar para validação
      setQuizLoading(true);
      try {
        const respostas = quizPerguntas.map(p => ({
          perguntaId: p.id,
          resposta: novasRespostas[p.id] || '',
        }));
        const data = await verifyActivationQuiz(token, respostas);
        if (data.aprovado || data.dadosDivergentes) {
          if (data.dadosDivergentes) {
            setHasDivergentData(true);
          }
          setStep(PASSWORD_STEP);
        } else if (data.bloqueado) {
          setQuizBloqueado(true);
          setQuizMensagem(data.mensagem || '');
        } else {
          setQuizMensagem(data.mensagem || '');
          // Resetar quiz para nova tentativa
          setQuizStep(0);
          setQuizRespostas({});
          // Recarregar perguntas (opções aleatórias novas)
          try {
            const quizData = await getActivationQuizQuestions(token);
            setQuizPerguntas(quizData.perguntas);
          } catch (e) { console.warn('[AtivarPage] Erro ao carregar quiz de ativacao:', String(e)); }
        }
      } catch {
        setQuizMensagem('Erro ao validar respostas. Tente novamente.');
      } finally {
        setQuizLoading(false);
      }
    }
  }, [quizSelecionada, quizStep, quizPerguntas, quizRespostas, token, PASSWORD_STEP]);

  // ── JWT temporário para registro de PassKey após ativação com senha ──
  const activationJwtRef = useRef('');
  const [passkeyRegLoading, setPasskeyRegLoading] = useState(false);
  const [passkeyRegError, setPasskeyRegError] = useState('');


  // ── Fase B: Limpeza de sessão anterior ao montar (só para fluxo de convite) ──
  useEffect(() => {
    if (fromLogin) return; // Não limpar sessão quando veio do login
    if (isAuthenticated) return; // Se estiver autenticado, mostrar aviso antes de limpar session

    // Remove qualquer sessão anterior para garantir que o fluxo de ativação
    // parte do zero — evita reuso de JWT stale
    localStorage.removeItem('ac_token');
    localStorage.removeItem('ac_escolaId');
    localStorage.removeItem('ac_nomeEscola');
    localStorage.removeItem('ac_perfil');
    localStorage.removeItem('ac_cargo');
    // Limpar progresso do wizard anterior (reset de escola)
    localStorage.removeItem('sw_wizard_etapa');
    localStorage.removeItem('ac_tokenExpiraEm');
    sessionStorage.removeItem('sw_intro_seen');
    sessionStorage.removeItem('bonding_session_id');
    localStorage.removeItem('bonding_session_id');
    // Não chamar logout() do Zustand aqui para não causar re-render antes do verify
  }, []);

  // ── Verificação: se usuário logado, checar se o convite é dele mesmo ──
  useEffect(() => {
    if (!checkingSession || !token || !user?.email) {
      if (checkingSession) setCheckingSession(false);
      return;
    }

    const checkSameUser = async () => {
      try {
        const data = await verifyActivationToken<any>(token);

        // Caso 1: Conta já ativa (backend retorna 200 com alreadyActive: true)
        if (data?.alreadyActive && data?.email) {
          if (data.email.toLowerCase() === user.email.toLowerCase()) {
            console.log('[AtivarPage] Conta já ativa do mesmo usuário logado. Redirecionando ao dashboard...');
            const target = getRedirectPath(user.perfil || '', (user as any)?.cargo);
            navigate(target, { replace: true });
            return;
          }
          // Diferente → mostrar dialog
          setShowAlreadyLoggedInDialog(true);
          setCheckingSession(false);
          return;
        }

        // Caso 2: Token válido (conta PENDENTE) — comparar email
        if (data?.email && data.email.toLowerCase() === user.email.toLowerCase()) {
          console.log('[AtivarPage] Convite do mesmo usuário logado. Redirecionando ao dashboard...');
          const target = getRedirectPath(user.perfil || '', (user as any)?.cargo);
          navigate(target, { replace: true });
          return;
        }
      } catch {
        // Backend não encontrou o token (404) → verificar cache local como fallback
        const cachedUser = localStorage.getItem('ac_user_info_' + token);
        if (cachedUser) {
          try {
            const cached = JSON.parse(cachedUser);
            if (cached.email && cached.email.toLowerCase() === user.email.toLowerCase()) {
              console.log('[AtivarPage] Cache confirma: mesmo usuário. Redirecionando ao dashboard...');
              const target = getRedirectPath(user.perfil || '', (user as any)?.cargo);
              navigate(target, { replace: true });
              return;
            }
          } catch (e) { console.warn('[AtivarPage] Erro ao validar etapa de ativacao:', String(e)); }
        }
      }
      // Usuários diferentes ou indeterminado → mostrar dialog
      setShowAlreadyLoggedInDialog(true);
      setCheckingSession(false);
    };
    checkSameUser();
  }, [checkingSession, token, user]);

  useEffect(() => {
    if (fromLogin) return; // fromLogin não usa token de ativação
    if (checkingSession || showAlreadyLoggedInDialog) return; // Aguardar check de mesmo usuário ou dialog aberto
    if (!token) { setError('Link inválido. Nenhum token encontrado.'); return; }

    const verify = async () => {
      try {
        console.log('[AtivarPage] Chamando verify-token...', { token: token.slice(0, 8) + '...' });
        const data = await verifyActivationToken<any>(token);

        // Conta já ativa → exibir mensagem clara ao usuário com email preservado
        if (data?.alreadyActive) {
          setUserInfo(data as UserInfo);
          setError('Esta conta já está ativa no sistema. Faça seu login.');
          return;
        }

        // Atualiza userInfo e persiste cache local
        setUserInfo(data as UserInfo);
        localStorage.setItem('ac_user_info_' + token, JSON.stringify(data));

        // Se estiver no celular, mas não for standalone, salvar token para handoff (para qualquer perfil)
        if (isMobile && !isStandalone) {
          console.log('[AtivarPage] Handoff: salvando token de ativação pendente no localStorage');
          localStorage.setItem('ac_activation_token_pending', token);
          if (inviteCode) {
            localStorage.setItem('ac_activation_code_pending', inviteCode);
          }
        }

        // ── Observe: link aberto
        trackEvent({ source: 'link_ativacao', action: 'opened', targetId: data.id, targetType: 'usuario' });

        // ── Branch: tem wizard configurado para este perfil?
        const wizard = getWizardRoute(data.perfil, data.cargo);
        // Only update wizard route if it actually changes
        setWizardRoute(prev => (prev !== wizard ? wizard : prev));

        // Forçar fluxo padrão de ativação:
        // links antigos com acessoDireto=true estavam confundindo com tela de login.
        setAcessoDireto(false);

        if (wizard) {
          // Perfil com wizard → ativar sem senha + redirecionar
          await handleActivateByToken(wizard);
        } else if (data.perfil === 'RESPONSAVEL') {
          setIsResponsavel(true);
          if ((data as any).preferencias?.dadosDivergentes) {
             setHasDivergentData(true);
             setStep(prev => (prev !== PASSWORD_STEP ? PASSWORD_STEP : prev));
          } else {
             try {
               const quizData = await getActivationQuizQuestions(token);
               if (quizData.bypass || !quizData.perguntas?.length) {
                 setStep(2);
               } else {
                 setQuizPerguntas(quizData.perguntas);
                 setQuizNomeAluno(quizData.nomeAluno || '');
                 setStep(prev => (prev !== 1 ? 1 : prev));
               }
             } catch {
               setStep(2);
             }
           }
        } else {
          // Perfil sem wizard → fluxo padrão (criar senha)
          setStep(prev => (prev !== 1 ? 1 : prev));
        }
      } catch (err: any) {
        const status = err.response?.status || 0;
        const data = err.response?.data || {};
        const msg: string = data?.message || err.message || '';
        const backendEmail: string = data?.email || '';
        const alreadyActive: boolean = data?.alreadyActive === true;
        const cachedUser = localStorage.getItem('ac_user_info_' + token);
        let cachedEmail = backendEmail;
        if (!cachedEmail && cachedUser) {
          try {
            cachedEmail = JSON.parse(cachedUser).email || '';
          } catch {}
        }

        console.log('[AtivarPage] verify-token erro:', { status, msg, backendEmail, alreadyActive, cachedEmail });

        if (localStorage.getItem('ac_just_activated_' + token) === 'true') {
          if (cachedUser) {
            try {
              setUserInfo(JSON.parse(cachedUser));
            } catch (e) { console.warn('[AtivarPage] Erro ao parsear cachedUser:', String(e)); }
          }
          setStep(DONE_STEP);
          return;
        }

        // Conta já ativa (400 com alreadyActive=true) ou link expirado/inválido
        if (alreadyActive || msg.includes('já foi ativada') || msg.includes('já ativa')) {
          if (backendEmail) {
            setUserInfo({ email: backendEmail } as any);
          }
          setError('Esta conta já está ativa no sistema. Faça seu login.');
        } else if (status === 404 || msg.includes('inválido') || msg.includes('utilizado') || msg.includes('Not Found')) {
          setError('Este link de ativação já foi utilizado ou expirou. Solicite um novo link ao administrador da escola ou acesse o login.');
        } else if (msg.includes('expirado')) {
          setError('Link de ativação expirado (válido por 72h). Solicite um novo link ao administrador da escola.');
        } else if (msg.includes('já está em setup')) {
          if ((userInfo as any)?.acessoDireto) {
            navigate(getRedirectPath(userInfo?.perfil || '', userInfo?.cargo || undefined), { replace: true });
          } else {
            const wizard = getWizardRoute(userInfo?.perfil || '', userInfo?.cargo || '');
            if (wizard) navigate(wizard, { replace: true });
            else setError('Conta em processo de ativação.');
          }
        } else {
          setError(msg || 'Erro ao verificar link.');
        }
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, fromLogin, showAlreadyLoggedInDialog]);

  // ── Fluxo fromLogin: usuário já autenticado, verificar/registrar passkey ──
  useEffect(() => {
    if (!fromLogin) return;
    if (!isAuthenticated || !user) {
      navigate('/login', { replace: true });
      return;
    }

    if (!isPasskeyEnabled) {
      // Se passkey estiver desativado globalmente, vai direto para o dashboard
      const target = getRedirectPath(user.perfil || '', (user as any).cargo || undefined);
      navigate(target, { replace: true });
      return;
    }

    const checkPasskey = async () => {
      try {
        const authToken = (localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token'));
        if (!authToken) { navigate('/login', { replace: true }); return; }
        setPasskeyPhase('checking');
        passkeyJwtRef.current = authToken;
        passkeyWizardRef.current = getRedirectPath(user.perfil || '', (user as any).cargo || undefined);
        const pkData = await getPasskeyStatus({ bearerToken: authToken }).catch(() => ({ temPasskey: false }));
        if (pkData.temPasskey) {
          // Tem passkey → auto-trigger verificação
          setPasskeyPhase('challenging');
          // Pequeno delay para garantir render antes do dialog do browser
          setTimeout(() => executePasskeyChallenge(), 600);
        } else {
          if (user?.email) {
            clearPasskeyOnThisDevice(user.email);
          }
          // Não tem passkey → auto-trigger cadastro
          setFromLoginNeedsRegister(true);
          setPasskeyPhase('challenging');
          setTimeout(() => executePasskeyRegisterFromLogin(), 600);
        }
      } catch {
        // Falha ao checar → ir direto pro dashboard
        navigate(getRedirectPath(user.perfil || '', (user as any).cargo || undefined), { replace: true });
      }
    };
    checkPasskey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLogin, isAuthenticated]);





  async function handleActivateByToken(wizardFallback: string) {
    try {
      const data = await activateByToken<any>(token);
      localStorage.setItem('ac_just_activated_' + token, 'true');

      // Backend retorna wizardRoute quando requiresSetup=true
      const targetRoute = data.wizardRoute || wizardFallback;

      // Salvar prazo de expiração para o wizard exibir alerta de countdown
      if (data.tokenExpiraEm) {
        localStorage.setItem('ac_tokenExpiraEm', data.tokenExpiraEm);
      }

      // ── Fase A: Verificar se usuário já tem Passkey antes de liberar acesso ──
      // NOTA: Se requiresSetup=true (conta resetada/nova), pular verificação
      // porque passkeys antigos são inválidos após reset pelo admin
      if (data.access_token && !data.requiresSetup) {
        try {
          setPasskeyPhase('checking');
          const pkData = await getPasskeyStatus({ bearerToken: data.access_token })
            .catch(() => ({ temPasskey: false }));

          if (pkData.temPasskey) {
            // Usuário JÁ TEM Passkey → exigir verificação antes de liberar JWT
            passkeyJwtRef.current = data.access_token;
            passkeyWizardRef.current = targetRoute;
            setPasskeyPhase('challenging');
            // NÃO persiste JWT ainda — só após Passkey verificada
            // O componente de challenge vai chamar executePasskeyChallenge()
            return;
          }
          if (data.usuario?.email) {
            clearPasskeyOnThisDevice(data.usuario.email);
          }
        } catch {
          // Falha ao checar passkey → prosseguir normalmente (primeiro acesso)
        }
        setPasskeyPhase('idle');
      }

      // Sem Passkey ou primeiro acesso → login direto
      await finalizeLogin(data.access_token, data.usuario, targetRoute);
    } catch (err: any) {
      setPasskeyPhase('idle');
      const message = err?.response?.data?.message || err?.message || 'Erro ao ativar conta.';
      if (
        err?.response?.status === 400 &&
        (message.includes('ja foi ativada') ||
          message.includes('ja ativa') ||
          message.includes('já foi ativada') ||
          message.includes('já ativa'))
      ) {
        setError('Este link ja foi utilizado. A conta ja esta ativa - faca login para continuar.');
        return;
      }
      setError(message);
    }
  }

  /** Persiste JWT e redireciona ao wizard */
  async function finalizeLogin(accessToken: string, usuario: any, targetRoute: string) {
    if (fromLogin) {
      // Usuário já está autenticado no store (veio do login)
      navigate(targetRoute, { replace: true });
      return;
    }
    if (accessToken && usuario) {
      const userData = {
        id: usuario.id,
        nome: usuario.nome,
        email: usuario.email,
        perfil: usuario.perfil,
        roles: [usuario.perfil],
        role: usuario.perfil,
        cargo: usuario.cargo || undefined,
        escolaId: usuario.escolaId || undefined,
        nomeEscola: usuario.nomeEscola || undefined,
        municipioId: usuario.municipioId || undefined,
        estadoId: usuario.estadoId || undefined,
        redeId: usuario.redeId || undefined,
        acessoDireto: usuario.acessoDireto ?? false,
        statusConta: usuario.statusConta,
      };
      saveAccountToLocal(userData, 'email');
      useAuthStore.getState().login(accessToken, userData);
      try {
        await useAuthStore.getState().checkAuth();
      } catch (e) { console.warn('[AtivarPage] Erro ao checkAuth pos-ativacao:', String(e)); }
    }

    // Se estiver no celular, mas não em modo Standalone (PWA), exigir a instalação do PWA para qualquer perfil
    const skippedPwaInstall = localStorage.getItem('ac_skip_pwa_install') === 'true';
    if (isMobile && !isStandalone && !skippedPwaInstall) {
      navigate(`/install?redirect=${encodeURIComponent(targetRoute)}`, { replace: true });
      return;
    }

    navigate(targetRoute, { replace: true });
  }

  /** Executa o desafio WebAuthn com o JWT temporário */
  async function executePasskeyChallenge() {
    const jwt = passkeyJwtRef.current;
    if (!jwt) return;
    try {
      // 1. Obter challenge do backend
      const requestOptions = { bearerToken: jwt };
      const optData = await requestPasskeyAuthOptions(requestOptions);
      const authOptions = (optData as any).options || optData;

      // 2. Executar WebAuthn no browser (biometria/PIN)
      const assertion = await startAuthentication({ optionsJSON: authOptions as any });

      // 3. Verificar no backend
      const verifyResult = await verifyPasskeyAuthentication(assertion, requestOptions);
      if (verifyResult.ok === false) throw new Error('Verificação biométrica falhou');

      // 4. Sucesso → agora sim, persistir JWT e navegar
      setPasskeyPhase('success');
      setTimeout(() => {
        sessionStorage.setItem('ac_accesskey_verified', 'true');
        finalizeLogin(jwt, userInfo, passkeyWizardRef.current);
      }, 1500);
    } catch (e: any) {
      const novas = passkeyAttempts + 1;
      setPasskeyAttempts(novas);
      if (novas >= 3) {
        // Máximo de tentativas — invalidar tudo
        setPasskeyPhase('failed');
        passkeyJwtRef.current = '';
        setPasskeyError('Máximo de tentativas atingido. Solicite um novo convite ao administrador.');
      } else if (fromLogin && novas === 1) {
        // Primeira falha com fromLogin → auto-fallback: registrar passkey neste dispositivo
        setFromLoginNeedsRegister(true);
        setPasskeyError('');
        setTimeout(() => executePasskeyRegisterFromLogin(), 500);
      } else {
        const msg = e.name === 'NotAllowedError'
          ? `Verificação cancelada. Tentativa ${novas}/3.`
          : `Falha na verificação. Tentativa ${novas}/3.`;
        setPasskeyError(msg);
      }
    }
  }

  /** Registra passkey para usuários vindos do login (sem passkey anterior) */
  async function executePasskeyRegisterFromLogin() {
    const jwt = passkeyJwtRef.current;
    if (!jwt) return;
    try {
      const ok = await registerPasskey(jwt);
      if (ok) {
        setPasskeyPhase('success');
        sessionStorage.setItem('ac_accesskey_verified', 'true');
        setTimeout(() => {
          navigate(passkeyWizardRef.current, { replace: true });
        }, 1500);
      } else {
        setPasskeyError(passkeyHookError || 'Erro ao registrar chave de acesso.');
      }
    } catch (e: any) {
      setPasskeyError(e.message || 'Erro inesperado.');
    }
  }

  // ── Fluxo de senha (fallback para perfis sem wizard)
  const getSenhaStrength = () => {
    if (!senha) return { level: 0, label: '', color: '#e5e7eb' };
    if (senha.length < 6) return { level: 1, label: 'Fraca', color: '#ef4444' };
    const score = [senha.length >= 8, /[A-Z]/.test(senha), /[0-9]/.test(senha), /[!@#$%^&*(),.?":{}|<>]/.test(senha)].filter(Boolean).length;
    if (score <= 1) return { level: 1, label: 'Fraca', color: '#ef4444' };
    if (score === 2) return { level: 2, label: 'Media', color: '#f59e0b' };
    if (score === 3) return { level: 3, label: 'Boa', color: '#3b82f6' };
    return { level: 4, label: 'Forte', color: '#16a34a' };
  };

  const strength = getSenhaStrength();
  const senhaValida = senha.length >= 8 && senha === senhaConfirm;

  const handleActivate = async () => {
    if (!senhaValida) return;
    setLoading(true);
    try {
      const fp = [navigator.userAgent, screen.width, screen.height, screen.colorDepth, Intl.DateTimeFormat().resolvedOptions().timeZone].join('|');
      const ativado = await activateWithPassword<any>({ token, senha, fingerprint: fp });
      setUserInfo(prev => prev ? { ...prev, ...ativado } : ativado);
      localStorage.setItem('ac_just_activated_' + token, 'true');

      // Observe: conta ativada com sucesso
      trackEvent({ source: 'link_ativacao', action: 'completed', targetId: ativado?.id || userInfo?.id, targetType: 'usuario' });

      // Login automático para obter JWT (necessário para registrar PassKey)
      // e para já entrar no painel do professor após definir senha.
      let emailParaLogin = '';
      try {
        emailParaLogin = ativado?.email || userInfo?.email || '';
        if (emailParaLogin) {
          const auth = await loginAfterActivation(emailParaLogin, senha);
            const loggedUser = auth.user || auth.usuario;
            if (auth.access_token && loggedUser) {
              activationJwtRef.current = auth.access_token;
              localStorage.setItem('ac_token', auth.access_token);
              localStorage.setItem('usuario', JSON.stringify(loggedUser));

              const perfilAtivado = String(loggedUser.perfil || ativado?.perfil || userInfo?.perfil || '');
              
              const userData = {
                id: loggedUser.id,
                nome: loggedUser.nome,
                email: loggedUser.email,
                perfil: loggedUser.perfil,
                roles: [loggedUser.perfil],
                role: loggedUser.perfil,
                cargo: loggedUser.cargo || undefined,
                escolaId: loggedUser.escolaId || undefined,
                nomeEscola: loggedUser.nomeEscola || undefined,
                municipioId: loggedUser.municipioId || undefined,
                estadoId: loggedUser.estadoId || undefined,
                redeId: loggedUser.redeId || undefined,
                acessoDireto: loggedUser.acessoDireto ?? false,
                statusConta: loggedUser.statusConta,
              };
              saveAccountToLocal(userData, 'email');
              useAuthStore.getState().login(auth.access_token, userData);
              try {
                await useAuthStore.getState().checkAuth();
              } catch (e) { console.warn('[AtivarPage] Erro ao checkAuth pos-link-magico:', String(e)); }

              const target = getRedirectPath(perfilAtivado, loggedUser.cargo || undefined);
              const skippedPwaInstall = localStorage.getItem('ac_skip_pwa_install') === 'true';
              if (isMobile && !isStandalone && !skippedPwaInstall) {
                navigate(`/install?redirect=${encodeURIComponent(target)}`, { replace: true });
                return;
              }

              // Perfis administrativos: fluxo obrigatório de segurança (QR + Passkey + Push)
              const perfisComOnboarding = ['ADMIN'];
              if (perfisComOnboarding.includes(perfilAtivado) && !loggedUser.acessoDireto) {
                localStorage.setItem('ac_security_onboarding_pending', '1');
                navigate('/onboarding/security', { replace: true });
                return;
              }

              navigate(target, { replace: true });
              return;
            }
        }
      } catch { /* login automático falhou — não é bloqueante */ }

      const target = `/login?email=${encodeURIComponent(emailParaLogin)}&activated=1`;
      const skippedPwaInstall = localStorage.getItem('ac_skip_pwa_install') === 'true';
      if (isMobile && !isStandalone && !skippedPwaInstall) {
        navigate(`/install?redirect=${encodeURIComponent(target)}`, { replace: true });
      } else {
        navigate(target, { replace: true });
      }
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  /** Registra PassKey após ativação com senha (step 2) */
  const handleRegisterPasskeyAfterActivation = async () => {
    const jwt = activationJwtRef.current;
    if (!jwt) {
      setStep(DONE_STEP);
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    setPasskeyRegLoading(true);
    setPasskeyRegError('');
    try {
      const ok = await registerPasskey(jwt);
      setPasskeyRegLoading(false);
      if (ok) {
        sessionStorage.setItem('ac_accesskey_verified', 'true');
        const emailForDevice = (userInfo?.email || '').trim().toLowerCase();
        if (emailForDevice) {
          markPasskeyOnThisDevice(emailForDevice);
        }
        if (userInfo) {
          const u = userInfo as any;
          const userData = {
            id: u.id,
            nome: u.nome,
            email: u.email,
            perfil: u.perfil,
            roles: [u.perfil],
            role: u.perfil,
            cargo: u.cargo || undefined,
            escolaId: u.escolaId || undefined,
            nomeEscola: u.nomeEscola || undefined,
            municipioId: u.municipioId || undefined,
            estadoId: u.estadoId || undefined,
            redeId: u.redeId || undefined,
            acessoDireto: u.acessoDireto ?? false,
            statusConta: u.statusConta,
          };
          saveAccountToLocal(userData, 'email');
        }
        trackEvent({ source: 'link_ativacao', action: 'passkey_registered', targetId: userInfo?.id, targetType: 'usuario' });
        setStep(DONE_STEP);
        setTimeout(() => {
          const perfil = userInfo?.perfil;
          if (perfil === 'PROFESSOR') navigate('/professor/dashboard');
          else navigate('/login');
        }, 2000);
      } else {
        setPasskeyRegError(passkeyHookError || 'Erro ao cadastrar chave de segurança.');
      }
    } catch (e: any) {
      setPasskeyRegLoading(false);
      setPasskeyRegError(e.message || 'Erro ao cadastrar chave de segurança.');
    }
  };

  const hasAutoTriggeredPasskeyRef = useRef(false);

  // Auto-aciona o registro da Passkey quando chega no step correspondente
  useEffect(() => {
    if (step === PASSKEY_STEP && !error && !hasAutoTriggeredPasskeyRef.current) {
      hasAutoTriggeredPasskeyRef.current = true;
      handleRegisterPasskeyAfterActivation();
    }
  }, [step, PASSKEY_STEP, error]);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: '1.5px solid #e2e8f0', fontSize: '0.84rem',
    background: '#f8fafc', color: '#1e293b', outline: 'none',
    transition: 'border-color .2s, box-shadow .2s',
  };

  const handleConfirmDisconnectAndActivate = async () => {
    const currentToken = token;
    const currentCode = inviteCode;

    // Inativar sessão anterior no backend sem disparar interceptor 401
    try {
      const jwtToken = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
      if (jwtToken) {
        await api.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${jwtToken}` }
        }).catch(() => {});
      }
    } catch (e) { console.warn('[AtivarPage] Erro no fluxo de ativacao:', String(e)); }

    // Limpar storage da sessão antiga
    localStorage.removeItem('ac_token');
    localStorage.removeItem('ac_user');
    localStorage.removeItem('ac_escolaId');
    localStorage.removeItem('ac_nomeEscola');
    localStorage.removeItem('ac_perfil');
    localStorage.removeItem('ac_cargo');
    localStorage.removeItem('sw_wizard_etapa');
    localStorage.removeItem('ac_tokenExpiraEm');
    sessionStorage.removeItem('sw_intro_seen');
    sessionStorage.removeItem('bonding_session_id');
    localStorage.removeItem('bonding_session_id');

    // Atualizar authStore sem disparar redirecionamento global do interceptor
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
      escolaId: null,
      nomeEscola: null,
      perfil: null,
      cargo: null,
      isImpersonating: false,
      isLoading: false,
    });

    // Re-preservar token e código do novo convite
    if (currentToken) {
      sessionStorage.setItem('ac_activation_token', currentToken);
    }
    if (currentCode) {
      sessionStorage.setItem('ac_activation_code', currentCode);
    }

    // Fechar dialog e liberar verificação do novo convite na mesma página
    setShowAlreadyLoggedInDialog(false);
    setCheckingSession(false);
  };

  const handleKeepCurrentAccount = () => {
    let activePerfil = user?.perfil;
    let activeCargo = (user as any)?.cargo;

    if (!activePerfil) {
      try {
        const storedStr = localStorage.getItem('usuario') || localStorage.getItem('ac_user') || localStorage.getItem('user');
        if (storedStr) {
          const parsed = JSON.parse(storedStr);
          activePerfil = parsed?.perfil;
          activeCargo = parsed?.cargo;
        }
      } catch (_) {}
    }

    const target = getRedirectPath(activePerfil || '', activeCargo);
    console.log('[AtivarPage] Manter conta atual -> redirecionando para:', target, { activePerfil, activeCargo });
    navigate(target, { replace: true });
  };

  // ── Se houver uma sessão ativa já logada, avisar ao usuário ─────────────────
  if (showAlreadyLoggedInDialog) {
    return (
      <div style={SOVEREIGN_BG}>
        <div style={{ ...SOVEREIGN_CARD, maxWidth: 460, padding: '36px 30px', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
            background: '#fffbeb', border: '2px solid #fef3c7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(245,158,11,0.08)',
          }}>
            <AlertTriangle size={28} style={{ color: '#d97706' }} />
          </div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: '0 0 8px', letterSpacing: '-0.02em', lineHeight: 1.25 }}>
            Sessão Ativa Detectada
          </h2>
          <p style={{ color: '#64748b', fontSize: '.84rem', lineHeight: 1.5, marginBottom: 20 }}>
            Você já está conectado como <strong style={{ color: '#0f172a' }}>{user?.nome}</strong> ({user?.email}).
          </p>
          <p style={{ color: '#64748b', fontSize: '.84rem', lineHeight: 1.5, marginBottom: 24 }}>
            Para ativar este novo convite, é necessário desconectar da conta atual. O que deseja fazer?
          </p>

          <button
            type="button"
            onClick={handleConfirmDisconnectAndActivate}
            data-testid="btn-desconectar-ativar"
            data-lab-action="disconnect-and-activate"
            data-lab-safe-action="true"
            style={{
              width: '100%', padding: '14px 20px', borderRadius: 14, border: 'none',
              background: 'linear-gradient(135deg, #6c5ce7, #5a4fcf)',
              color: '#ffffff', fontSize: '.9rem', fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 6px 20px rgba(108,92,231,0.25)',
              transition: 'all .2s',
              marginBottom: 12
            }}
          >
            Desconectar e Ativar Novo Convite
          </button>

          <button
            onClick={handleKeepCurrentAccount}
            style={{
              width: '100%', padding: '14px 20px', borderRadius: 14,
              border: '1.5px solid #e2e8f0', background: '#ffffff',
              color: '#475569', fontSize: '.9rem', fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all .2s'
            }}
          >
            Manter Minha Conta Atual
          </button>
        </div>
      </div>
    );
  }

  // ── fromLogin: spinner enquanto verifica passkey ─────────────────
  // ── fromLogin: spinner enquanto verifica passkey ─────────────────
  if (fromLogin && (passkeyPhase === 'idle' || passkeyPhase === 'checking')) {
    return (
      <div style={SOVEREIGN_BG}>
        {/* Top Bar — Logo centralizada */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '14px clamp(16px, 3vw, 32px)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, flexShrink: 0 }}>
              <AcShieldLogo />
            </div>
            <div>
              <h1 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Aluno <span style={{ color: '#a78bfa' }}>Conectado</span>
              </h1>
              <p style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', margin: '2px 0 0', fontWeight: 500 }}>
                Plataforma Segura de Acesso
              </p>
            </div>
          </div>
        </div>

        <div style={SOVEREIGN_CARD}>
          <div style={{
            width: 38, height: 38, border: '3px solid rgba(108,92,231,0.1)',
            borderTopColor: '#6c5ce7', borderRadius: '50%',
            margin: '0 auto 16px', animation: 'spin .8s linear infinite',
          }} />
          <h2 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            Verificando Seguranca
          </h2>
          <p style={{ color: '#64748b', fontSize: '.82rem', margin: 0 }}>
            Ola, {user?.nome || 'usuario'}! Aguarde um momento.
          </p>
        </div>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes pan-bg { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
          @keyframes fade-in-up { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </div>
  );
}
  // ── Acesso Direto Flow ────────────────────────────────────────


  if (acessoDireto && userInfo) {
    return (
      <AcessoDiretoFlow token={token} userInfo={userInfo} autoEmailCode={inviteCode} />
  );
}
  // ── Passkey Challenge Screen ──────────────────────────────────────
  if (passkeyPhase === 'challenging' || passkeyPhase === 'success') {
    return (
      <div style={SOVEREIGN_BG}>
        {/* Diálogo de confirmação de desconexão */}
        {showLogoutDialog && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
            backdropFilter: 'blur(8px)',
          }} onClick={() => setShowLogoutDialog(false)}>
            <div onClick={e => e.stopPropagation()} style={{
              maxWidth: 380, width: '100%', borderRadius: 20, padding: '2rem',
              background: '#ffffff', border: '1px solid rgba(0,0,0,0.05)',
              textAlign: 'center',
              boxShadow: '0 24px 48px rgba(0,0,0,0.1)',
              animation: 'fade-in-up 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
                background: '#fef2f2', border: '1px solid #fecaca',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <LogOut size={26} style={{ color: '#ef4444' }} />
              </div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>
                Deseja sair?
              </h3>
              <p style={{ color: '#64748b', fontSize: '.88rem', lineHeight: 1.6, marginBottom: 24 }}>
                Sua sessao atual sera encerrada. Para voltar, voce precisara fazer o login novamente.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowLogoutDialog(false)} style={{
                  flex: 1, padding: '12px', borderRadius: 12,
                  border: '1px solid #e2e8f0', background: '#f8fafc',
                  color: '#475569', cursor: 'pointer', fontWeight: 600, fontSize: '.88rem',
                }}>Cancelar</button>
                <button onClick={async () => {
                  setShowLogoutDialog(false);
                  await useAuthStore.getState().logout();
                  navigate('/login');
                }} style={{
                  flex: 1, padding: '12px', borderRadius: 12, border: 'none',
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '.88rem',
                  boxShadow: '0 4px 14px rgba(239,68,68,0.3)',
                }}>Desconectar</button>
              </div>
            </div>
          </div>
        )}

        {/* Top Bar — Logo esquerda + Sair direita */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px clamp(16px, 3vw, 32px)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, flexShrink: 0 }}>
              <AcShieldLogo />
            </div>
            <div>
              <h1 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Aluno <span style={{ color: '#a78bfa' }}>Conectado</span>
              </h1>
              <p style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', margin: '2px 0 0', fontWeight: 500 }}>
                Plataforma Segura de Acesso
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowLogoutDialog(true)}
            title="Desconectar"
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: 8,
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
              backdropFilter: 'blur(12px)', cursor: 'pointer',
              color: 'rgba(255,255,255,0.6)', fontSize: '.76rem', fontWeight: 600,
              transition: 'all .2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
          >
            <LogOut size={14} />
            Sair
          </button>
        </div>

        <div style={SOVEREIGN_CARD}>

          {passkeyPhase === 'success' ? (
            <>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
                background: '#f0fdf4', border: '2px solid #bbf7d0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 20px rgba(34,197,94,0.08)',
              }}>
                <CheckCircle2 size={28} style={{ color: '#16a34a' }} />
              </div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#16a34a', margin: '0 0 6px', letterSpacing: '-0.02em' }}>Identidade Confirmada!</h2>
              <p style={{ color: '#64748b', fontSize: '.82rem' }}>Voce esta sendo direcionado ao seu ambiente seguro.</p>
            </>
          ) : (
            <>
              {/* Layout de duas colunas alinhado */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 20,
                textAlign: 'left',
                marginBottom: 20,
                width: '100%'
              }}>
                {/* Coluna Esquerda: Ícone de digital */}
                <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 90, height: 90, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(108,92,231,0.08) 0%, transparent 70%)',
                    pointerEvents: 'none',
                  }} />
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%', position: 'relative',
                    background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                    border: '1px solid #ddd6fe',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 0 20px rgba(108,92,231,0.06)',
                  }}>
                    <Fingerprint size={28} strokeWidth={1.5} style={{ color: '#6c5ce7' }} />
                  </div>
                </div>

                {/* Coluna Direita: Textos */}
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1e293b', margin: '0 0 6px', letterSpacing: '-0.02em', lineHeight: 1.25 }}>
                    {fromLoginNeedsRegister ? 'Cadastrar Chave de Segurança' : 'Verificação de Segurança'}
                  </h2>
                  <p style={{ color: '#64748b', fontSize: '.82rem', lineHeight: 1.4, margin: 0 }}>
                    Ola, <strong style={{ color: '#0f172a' }}>{userInfo?.nome || user?.nome}</strong>!
                  </p>
                  <p style={{ color: '#475569', fontSize: '.8rem', lineHeight: 1.4, margin: '4px 0 0' }}>
                    {fromLoginNeedsRegister
                      ? 'Cadastre sua chave de segurança (biometria ou rosto) para proteger e agilizar seus acessos.'
                      : 'Sua conta já possui uma chave de segurança registrada. Confirme sua identidade para continuar.'
                    }
                  </p>
                </div>
              </div>

              {passkeyError && (
                <div style={{
                  padding: '12px 16px', borderRadius: 12, marginBottom: 18,
                  background: '#fef2f2', border: '1px solid #fecaca',
                  color: '#dc2626', fontSize: '.82rem', lineHeight: 1.5,
                }}>
                  {passkeyError}
                </div>
              )}

              <button
                onClick={fromLoginNeedsRegister ? executePasskeyRegisterFromLogin : executePasskeyChallenge}
                disabled={!fromLoginNeedsRegister && passkeyAttempts >= 3}
                style={{
                  width: '100%', padding: '16px 24px', borderRadius: 16, border: 'none',
                  background: passkeyAttempts >= 3
                    ? '#f1f5f9'
                    : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                  color: passkeyAttempts >= 3 ? '#94a3b8' : '#ffffff',
                  fontSize: '1rem', fontWeight: 800,
                  cursor: passkeyAttempts >= 3 ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  boxShadow: passkeyAttempts < 3 ? '0 6px 24px rgba(108,92,231,0.35)' : 'none',
                  transition: 'all .3s cubic-bezier(0.16, 1, 0.3, 1)',
                  letterSpacing: '-0.01em',
                }}
              >
                <Fingerprint size={18} />
                {fromLoginNeedsRegister
                  ? (passkeyAttempts >= 3 ? 'Acesso Bloqueado' : 'Cadastrar Chave de Segurança')
                  : (passkeyAttempts >= 3 ? 'Acesso Bloqueado' : 'Verificar Identidade')
                }
              </button>

              {passkeyAttempts >= 3 && (
                <p style={{ color: '#ef4444', fontSize: '.78rem', marginTop: 18, lineHeight: 1.5, fontWeight: 500 }}>
                  Solicite um novo link de convite ao administrador.
                </p>
              )}

              {/* Fallback: registrar passkey NESTE dispositivo quando a verificacao falha */}
              {passkeyAttempts > 0 && passkeyAttempts < 3 && !fromLoginNeedsRegister && (
                <button
                  onClick={() => {
                    setFromLoginNeedsRegister(true);
                    setPasskeyError('');
                  }}
                  style={{
                    width: '100%', padding: '14px 24px', borderRadius: 16, marginTop: 12,
                    border: '1px solid #e2e8f0', background: '#f8fafc',
                    color: '#475569', fontSize: '.9rem', fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'all .2s',
                  }}
                >
                  <Fingerprint size={16} />
                  Cadastrar neste dispositivo
                </button>
              )}

              {/* Teste: pular passkey e ir direto ao dashboard */}
              {fromLogin && (
                <button
                  onClick={() => {
                    sessionStorage.setItem('ac_accesskey_verified', 'true');
                    navigate(passkeyWizardRef.current || getRedirectPath(user?.perfil || '', (user as any)?.cargo), { replace: true });
                  }}
                  style={{
                    width: '100%', padding: '12px 20px', borderRadius: 14, marginTop: 10,
                    border: 'none', background: 'transparent',
                    color: '#94a3b8', fontSize: '.8rem', fontWeight: 500,
                    cursor: 'pointer', textDecoration: 'underline',
                    transition: 'color .2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#64748b'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                >
                  Pular por enquanto
                </button>
              )}

              {/* Seguranca badge */}
              <div style={{ marginTop: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Shield size={13} style={{ color: '#94a3b8' }} />
                <span style={{ fontSize: '.78rem', color: '#94a3b8', fontWeight: 500 }}>
                  Conexao criptografada . Dados protegidos
                </span>
              </div>
            </>
          )}
        </div>

        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pan-bg { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        `}</style>
      </div>
    );
  }

  // ── Passkey Failed Screen ─────────────────────────────────────────
  if (passkeyPhase === 'failed') {
    return (
      <div style={SOVEREIGN_BG}>
        {/* Top Bar — Logo compacta */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '14px clamp(16px, 3vw, 32px)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, flexShrink: 0 }}>
              <AcShieldLogo />
            </div>
            <div>
              <h1 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Aluno <span style={{ color: '#a78bfa' }}>Conectado</span>
              </h1>
              <p style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', margin: '2px 0 0', fontWeight: 500 }}>
                Plataforma Segura de Acesso
              </p>
            </div>
          </div>
        </div>

        <div style={SOVEREIGN_CARD}>

          <div style={{
            width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
            background: '#fef2f2', border: '2px solid #fecaca',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(239,68,68,0.08)',
          }}>
            <ShieldX size={28} style={{ color: '#ef4444' }} />
          </div>
          <h2 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#ef4444', margin: '0 0 8px', letterSpacing: '-0.02em' }}>Verificacao Falhou</h2>
          <p style={{ color: '#64748b', fontSize: '.8rem', lineHeight: 1.5, marginBottom: 18 }}>
            {fromLogin
              ? 'Nao foi possivel completar a verificacao de seguranca. Voce pode tentar novamente ou acessar o sistema.'
              : 'Nao foi possivel confirmar sua identidade. Por seguranca, este link de ativacao foi invalidado. Solicite um novo convite ao administrador.'
            }
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexDirection: 'column' }}>
            {fromLogin && (
              <button onClick={() => {
                const dest = getRedirectPath(user?.perfil || '', (user as any)?.cargo || undefined);
                navigate(dest, { replace: true });
              }} style={{
                padding: '12px 24px', borderRadius: 12, border: 'none',
                background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                color: '#ffffff', cursor: 'pointer', fontSize: '.85rem', fontWeight: 700,
                boxShadow: '0 6px 24px rgba(108,92,231,0.3)',
              }}>
                Acessar Sistema
              </button>
            )}
            <button onClick={() => navigate('/login')} style={{
              padding: '12px 24px', borderRadius: 12, border: '1.5px solid #e2e8f0',
              background: '#f8fafc', color: '#475569', cursor: 'pointer',
              fontSize: '.85rem', fontWeight: 600,
              transition: 'all .2s',
            }}>
              Ir para Login
            </button>
          </div>
        </div>
        <style>{`
          @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pan-bg { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        `}</style>
      </div>
    );
  }

  // ── PWA & Device Checking ──────────────────────────────────────────
  if (userInfo && !isStandalone) {
    if (!isMobile) {
      if (userInfo && userInfo.perfil === 'RESPONSAVEL') {
        // ────────── DESKTOP REDIRECT TO MOBILE (QR CODE) ──────────
        return (
        <div style={SOVEREIGN_BG}>
          {/* Top Bar — Logo centralizada */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '14px clamp(16px, 3vw, 32px)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, flexShrink: 0 }}>
                <AcShieldLogo />
              </div>
              <div>
                <h1 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                  Aluno <span style={{ color: '#a78bfa' }}>Conectado</span>
                </h1>
                <p style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)', margin: '2px 0 0', fontWeight: 500 }}>
                  Plataforma Segura de Acesso
                </p>
              </div>
            </div>
          </div>

          <div style={{ ...SOVEREIGN_CARD, maxWidth: 500, padding: '36px 30px' }}>
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: 100, height: 100, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(108,92,231,0.08) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
              <div style={{
                width: 60, height: 60, borderRadius: '50%', position: 'relative',
                background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                border: '1px solid #ddd6fe',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 20px rgba(108,92,231,0.06)',
              }}>
                <Smartphone size={30} strokeWidth={1.5} style={{ color: '#6c5ce7' }} />
              </div>
            </div>

            <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
              Ativação Exclusiva pelo Celular
            </h2>
            
            <p style={{ color: '#475569', fontSize: '.84rem', lineHeight: 1.5, marginBottom: 18 }}>
              Olá, <strong style={{ color: '#0f172a' }}>{userInfo.nome}</strong>! Por questões de segurança e para receber notificações instantâneas em tempo real, a ativação da conta de responsável deve ser feita obrigatoriamente através do seu celular.
            </p>

            <div style={{
              background: '#ffffff', borderRadius: 20, padding: 20,
              display: 'inline-block', border: '1.5px solid #f1f5f9',
              boxShadow: '0 10px 30px rgba(108,92,231,0.05)',
              marginBottom: 20,
            }}>
              <StyledQrCode
                value={window.location.href}
                size={180}
                width={180}
                height={180}
                shape="square"
                style="rounded"
                icon="logo-shield-qrcode-backing"
                iconKind="preset"
                iconSize={80}
                hideBackgroundDots={false}
                foreground="#7c3aed"
                background="#ffffff"
                margin={0}
              />
            </div>

            <div style={{
              textAlign: 'left' as any, background: '#f8fafc', borderRadius: 14,
              padding: '14px 18px', border: '1px solid #e2e8f0', marginBottom: 20
            }}>
              <p style={{ fontSize: '.76rem', fontWeight: 700, color: '#475569', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                Como proceder:
              </p>
              <ol style={{ fontSize: '.8rem', color: '#64748b', margin: 0, paddingLeft: 16, lineHeight: 1.6 }}>
                <li>Abra a câmera do seu celular.</li>
                <li>Aponte para o QR Code para escanear o link.</li>
                <li>Abra o link e siga as instruções para instalar o aplicativo no celular.</li>
              </ol>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Shield size={13} style={{ color: '#94a3b8' }} />
              <span style={{ fontSize: '.76rem', color: '#94a3b8', fontWeight: 500 }}>
                Conexão segura . Ativação protegida
              </span>
            </div>
          </div>
        </div>
      );
      }
    } 
  }

  // ── Enquanto tem wizard configurado e não houve erro → spinner de redirecionamento
  if (wizardRoute && !error && passkeyPhase === 'idle') {
    return (
      <div style={SOVEREIGN_BG}>
        {/* Top Bar — Logo compacta */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 'clamp(14px, 3vw, 24px) clamp(16px, 3vw, 32px)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(10px, 2vw, 16px)' }}>
            <div style={{ width: 'clamp(40px, 5vw, 56px)', height: 'clamp(40px, 5vw, 56px)', flexShrink: 0 }}>
              <AcShieldLogo />
            </div>
            <div>
              <h1 style={{ fontSize: 'clamp(1.15rem, 3vw, 1.8rem)', fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Aluno <span style={{ color: '#a78bfa' }}>Conectado</span>
              </h1>
              <p style={{ fontSize: 'clamp(.72rem, 1.5vw, .95rem)', color: 'rgba(255,255,255,0.5)', margin: '2px 0 0', fontWeight: 500 }}>
                Ecossistema Educacional Seguro
              </p>
            </div>
          </div>
        </div>

        <div style={SOVEREIGN_CARD}>
          
          <div style={{
            width: 38, height: 38, border: '3px solid rgba(108,92,231,0.1)',
            borderTopColor: '#6c5ce7', borderRadius: '50%',
            margin: '0 auto 16px', animation: 'spin .8s linear infinite',
          }} />
          <h2 style={{ fontSize: '1rem', fontWeight: 800, color: '#1e293b', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            Preparando seu ambiente seguro...
          </h2>
          <p style={{ color: '#64748b', fontSize: '.8rem', margin: 0 }}>
            {userInfo ? `Olá, ${userInfo.nome}!` : 'Autenticando suas credenciais de acesso...'}
          </p>
        </div>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes pan-bg { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
          @keyframes fade-in-up { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </div>
    );
  }

  // ── Fluxo padrão (perfis sem wizard)
  return (
    <div style={SOVEREIGN_BG}>
      {/* Top Bar — Logo compacta */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(14px, 3vw, 24px) clamp(16px, 3vw, 32px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(10px, 2vw, 16px)' }}>
          <div style={{ width: 'clamp(40px, 5vw, 56px)', height: 'clamp(40px, 5vw, 56px)', flexShrink: 0 }}>
            <AcShieldLogo />
          </div>
          <div>
            <h1 style={{ fontSize: 'clamp(1.15rem, 3vw, 1.8rem)', fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Aluno <span style={{ color: '#a78bfa' }}>Conectado</span>
            </h1>
            <p style={{ fontSize: 'clamp(.72rem, 1.5vw, .95rem)', color: 'rgba(255,255,255,0.5)', margin: '2px 0 0', fontWeight: 500 }}>
              Ecossistema Educacional Seguro
            </p>
          </div>
        </div>
      </div>

      <div style={SOVEREIGN_CARD} className={!error && step === PASSWORD_STEP && userInfo ? 'password-step-card' : ''}>

        {/* Erro / Estado da Conta / Reenvio */}
        {error && (() => {
          const cachedUser = localStorage.getItem('ac_user_info_' + token);
          let cachedEmail = '';
          if (cachedUser) {
            try { cachedEmail = JSON.parse(cachedUser).email || ''; } catch {}
          }
          const email = userInfo?.email || cachedEmail;
          const isAlreadyActive = error.includes('já está ativa') || error.includes('já foi ativada') || error.includes('Já cadastrado') || error.includes('já ativa');

          if (isAlreadyActive) {
            return (
              <div style={{ textAlign: 'center', padding: '24px 20px', background: '#f0fdf4', borderRadius: 16, border: '1.5px solid #bbf7d0', boxShadow: '0 4px 12px rgba(21, 128, 61, 0.08)' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', margin: '0 auto 12px', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle2 size={28} style={{ color: '#15803d' }} />
                </div>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#15803d', marginBottom: 8, letterSpacing: '-0.3px' }}>
                  Sua Conta Já Está Ativa!
                </h2>
                <p style={{ color: '#374151', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: 18 }}>
                  {email ? (
                    <>A conta vinculada ao e-mail <strong style={{ color: '#111827' }}>{email}</strong> já concluiu a ativação e possui uma senha configurada.</>
                  ) : (
                    <>Esta conta já concluiu a ativação no sistema Aluno Conectado e já possui uma senha configurada.</>
                  )}
                  <br />Você já pode acessar o aplicativo ou o portal web normalmente.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                  <button 
                    onClick={() => navigate(email ? `/login?email=${encodeURIComponent(email)}&activated=1` : '/login')} 
                    style={{ padding: '12px 28px', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: '#ffffff', border: 'none', borderRadius: 12, fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 10px rgba(22, 163, 74, 0.25)', width: '100%', maxWidth: 280 }}
                  >
                    🔑 Ir para a Tela de Login
                  </button>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>
                    Identificação de segurança concluída com sucesso.
                  </span>
                </div>
              </div>
            );
          }

          return (
            <div style={{
              textAlign: 'center',
              padding: '28px 22px',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: 20,
              border: '1.5px solid #e2e8f0',
              boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.08)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Top Accent bar */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(90deg, #6366f1, #a855f7, #ec4899)',
              }} />

              {/* Icon Container */}
              <div style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                margin: '0 auto 16px',
                background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                border: '2px solid #fecaca',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(239, 68, 68, 0.15)',
              }}>
                <ShieldAlert size={28} style={{ color: '#dc2626' }} />
              </div>

              {/* Tag / Badge */}
              <div style={{ marginBottom: 10 }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 10px',
                  borderRadius: 9999,
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#b91c1c',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}>
                  <Shield size={11} /> Aluno Conectado • Segurança
                </span>
              </div>

              <h2 style={{
                fontSize: '1.15rem',
                fontWeight: 800,
                color: '#0f172a',
                marginBottom: 10,
                letterSpacing: '-0.02em',
              }}>
                Link de Convite Expirado ou Renovado
              </h2>

              <p style={{
                color: '#475569',
                fontSize: '0.84rem',
                lineHeight: 1.6,
                marginBottom: 20,
              }}>
                Por motivos de segurança e proteção de dados escolares da rede, os links de ativação do <strong>Aluno Conectado</strong> possuem validade de 72 horas ou são renovados quando um novo convite é emitido pela escola.
                <br /><br />
                {error && !error.includes('expirou') && !error.includes('expirado') ? (
                  <span style={{ color: '#64748b', fontSize: '0.78rem', display: 'block', marginBottom: 8 }}>
                    Detalhe técnico: {error}
                  </span>
                ) : null}
                Não se preocupe: você pode solicitar o reenvio automático de um novo link seguro agora mesmo para o WhatsApp e E-mail cadastrados na escola.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => setShowReenvioDialog(true)}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: '0.86rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: 300,
                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'all 0.2s',
                  }}
                >
                  <Send size={16} /> Solicitar Reenvio de Convite
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  style={{
                    padding: '10px 20px',
                    background: '#ffffff',
                    color: '#475569',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: 12,
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: 300,
                    transition: 'all 0.2s',
                  }}
                >
                  Ir para a Tela de Login
                </button>
              </div>
            </div>
          );
        })()}

        {/* Step 0 — verificando */}
        {!error && step === 0 && (
          <div style={{ textAlign: 'center', padding: '14px 0' }}>
            <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#6c5ce7', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
            <h2 style={{ fontSize: '.95rem', fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Preparando ambiente seguro...</h2>
            <p style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Autenticando suas credenciais</p>
          </div>
        )}

        {/* Step 1 (Responsável): Quiz de Verificação de Identidade */}
        {!error && step === 1 && isResponsavel && quizPerguntas.length > 0 && !quizBloqueado && (
          <div>
            {/* Header quiz */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', margin: '0 auto 12px', background: 'linear-gradient(135deg, #f0f7ff, #dbeafe)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #93c5fd' }}>
                <ShieldCheck size={24} style={{ color: '#2563eb' }} />
              </div>
              <h2 style={{ fontSize: '.95rem', fontWeight: 800, color: '#1e293b', marginBottom: 4, letterSpacing: '-0.02em' }}>Verificacao de Identidade</h2>
              <p style={{ color: '#64748b', fontSize: '.75rem', lineHeight: 1.5 }}>
                Responda as perguntas abaixo para confirmar sua identidade.
              </p>
            </div>

            {/* Progresso do quiz */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>
              {quizPerguntas.map((_: any, i: number) => (
                <div key={i} style={{
                  flex: 1, height: 4, borderRadius: 4,
                  background: i < quizStep ? '#16a34a' : i === quizStep ? '#2563eb' : '#e2e8f0',
                  transition: 'background .3s',
                }} />
              ))}
            </div>

            {/* Mensagem de erro (tentativa anterior) */}
            {quizMensagem && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: '#fef3c7', border: '1px solid #fde68a', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={16} style={{ color: '#d97706', flexShrink: 0 }} />
                <span style={{ fontSize: '.76rem', color: '#92400e', fontWeight: 600 }}>{quizMensagem}</span>
              </div>
            )}

            {/* Pergunta atual */}
            {quizPerguntas[quizStep] && (
              <div>
                <div style={{ fontSize: '.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
                  Pergunta {quizStep + 1} de {quizPerguntas.length}
                </div>
                <h3 style={{ fontSize: '.88rem', fontWeight: 700, color: '#1e293b', marginBottom: 14, lineHeight: 1.4 }}>
                  {quizPerguntas[quizStep].texto}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {quizPerguntas[quizStep].opcoes.map((opcao: string, idx: number) => {
                    const selected = quizSelecionada === opcao;
                    return (
                      <button
                        key={idx}
                        onClick={() => { setQuizSelecionada(opcao); setQuizMensagem(''); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '14px 16px', borderRadius: 12,
                          border: selected ? '2px solid #2563eb' : '1.5px solid #e2e8f0',
                          background: selected ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : '#fafafa',
                          cursor: 'pointer', transition: 'all .2s',
                          boxShadow: selected ? '0 0 0 3px rgba(37,99,235,0.1)' : 'none',
                        }}
                      >
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          border: selected ? '2px solid #2563eb' : '2px solid #cbd5e1',
                          background: selected ? '#2563eb' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all .2s',
                        }}>
                          {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                        </div>
                        <span style={{ fontSize: '.88rem', fontWeight: selected ? 700 : 600, color: selected ? '#1e40af' : '#374151' }}>
                          {opcao}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Botão avançar */}
            <button
              onClick={handleQuizNext}
              disabled={!quizSelecionada || quizLoading}
              style={{
                width: '100%', padding: '13px 20px', marginTop: 18,
                background: quizSelecionada ? 'linear-gradient(135deg, #2563eb, #60a5fa)' : '#f1f5f9',
                color: quizSelecionada ? '#fff' : '#94a3b8',
                border: 'none', borderRadius: 14, fontWeight: 700, fontSize: '.88rem',
                cursor: quizSelecionada ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: quizSelecionada ? '0 6px 24px rgba(37,99,235,0.3)' : 'none',
                transition: 'all .3s',
              }}
            >
              {quizLoading ? (
                <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Verificando...</>
              ) : quizStep < quizPerguntas.length - 1 ? (
                `Proxima (${quizStep + 1}/${quizPerguntas.length})`
              ) : (
                'Confirmar Respostas'
              )}
            </button>

            {/* Badge de seguranca */}
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Shield size={12} style={{ color: '#94a3b8' }} />
              <span style={{ fontSize: '.7rem', color: '#94a3b8', fontWeight: 500 }}>Verificacao anti-fraude</span>
            </div>
          </div>
        )}

        {/* Quiz Bloqueado */}
        {!error && step === 1 && isResponsavel && quizBloqueado && (
          <div style={{ textAlign: 'center', padding: '14px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 14px', background: '#fef2f2', border: '2px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShieldX size={28} style={{ color: '#ef4444' }} />
            </div>
            <h2 style={{ fontSize: '1rem', fontWeight: 800, color: '#ef4444', marginBottom: 8 }}>Verificacao Bloqueada</h2>
            <p style={{ color: '#64748b', fontSize: '.8rem', lineHeight: 1.6, marginBottom: 18 }}>
              {quizMensagem}
            </p>
            <div style={{ background: '#f0f4f8', borderRadius: 12, padding: '14px 18px', marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <HelpCircle size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
                <p style={{ fontSize: '.76rem', color: '#475569', margin: 0, lineHeight: 1.5 }}>
                  Entre em contato com a escola para solicitar um novo convite de ativacao.
                </p>
              </div>
            </div>
            <button onClick={() => navigate('/login')} style={{
              width: '100%', padding: '12px 20px', borderRadius: 12,
              border: '1.5px solid #e2e8f0', background: '#f8fafc',
              color: '#475569', fontSize: '.85rem', fontWeight: 600, cursor: 'pointer',
            }}>
              Ir para Login
            </button>
          </div>
        )}

        {/* Step: Carregando dados de ativação */}
        {!error && step === PASSWORD_STEP && !userInfo && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ width: 38, height: 38, border: '3px solid #e2e8f0', borderTopColor: '#6c5ce7', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
            <h2 style={{ fontSize: '.95rem', fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Carregando dados do convite...</h2>
            <p style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Aguarde enquanto preparamos seu acesso seguro</p>
          </div>
        )}

        {/* Step: Criar Senha */}
        {!error && step === PASSWORD_STEP && userInfo && (
          <div className="ativar-grid">
            <div className="ativar-left-col">
              {hasDivergentData && (
                <div style={{
                  padding: '12px 16px', borderRadius: 14, marginBottom: 18,
                  background: '#fffbeb', border: '1px solid #fef3c7',
                  color: '#b45309', fontSize: '.8rem', lineHeight: 1.5,
                  textAlign: 'left', display: 'flex', gap: 10, alignItems: 'flex-start'
                }}>
                  <AlertTriangle size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong style={{ color: '#92400e' }}>Acesso Restrito Temporário</strong><br />
                    Seus dados não correspondem aos registros. Seu cadastro será concluído com acesso restrito até a validação presencial ou atualização pela secretaria.
                  </div>
                </div>
              )}

              {/* Card de convite compacto */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(108,92,231,0.04) 0%, rgba(108,92,231,0.08) 100%)',
                borderRadius: 18, padding: '24px', border: '1px solid rgba(108,92,231,0.15)',
                marginBottom: 0, textAlign: 'center', boxShadow: 'inset 0 2px 10px rgba(255,255,255,0.7)'
              }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12, background: 'rgba(108,92,231,0.1)', padding: '6px 12px', borderRadius: 20 }}>
                  <ShieldCheck size={14} style={{ color: '#6c5ce7' }} />
                  <span style={{ color: '#6c5ce7', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>Convite Seguro</span>
                </div>
                <p style={{ color: '#0f172a', fontSize: '1.25rem', fontWeight: 800, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{userInfo.nome}</p>
                {userInfo.email && (
                  <p style={{ color: '#64748b', fontSize: '.8rem', margin: '4px 0 0', fontWeight: 500, wordBreak: 'break-all' }}>
                    {userInfo.email}
                  </p>
                )}
                {userInfo.escola && (
                  <>
                    <div style={{ width: 40, height: 2, background: 'linear-gradient(90deg, transparent, #c4b5fd, transparent)', margin: '14px auto' }} />
                    <p style={{ color: '#475569', fontSize: '.8rem', margin: 0, fontWeight: 600, letterSpacing: '.1px', lineHeight: 1.4 }}>
                      {userInfo.escola.nome}
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="ativar-right-col">
              <h2 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: 6, textAlign: 'center', color: '#0f172a', letterSpacing: '-0.03em' }}>Defina sua Senha de Acesso</h2>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 28, textAlign: 'center', lineHeight: 1.5 }}>
                Crie uma senha forte e fácil de lembrar para o seu ambiente educacional.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '16px 12px', alignItems: 'flex-start', marginBottom: 24, textAlign: 'left' }}>
                
                {/* Senha */}
                <label style={{ width: 120, textAlign: 'left', fontSize: '0.78rem', fontWeight: 700, color: '#475569', flexShrink: 0, marginTop: 10 }}>Senha</label>
                <div>
                  <div style={{ position: 'relative' }}>
                    <input type={showSenha ? 'text' : 'password'} name="senha" autoComplete="new-password" value={senha} onChange={e => setSenha(e.target.value)}
                      placeholder="Mínimo de 8 caracteres" style={{ ...inputStyle, paddingRight: 40 }}
                      onFocus={e => { e.target.style.borderColor = '#6c5ce7'; e.target.style.boxShadow = '0 0 0 3px rgba(108,92,231,0.1)'; }}
                      onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }} />
                    <button type="button" onClick={() => setShowSenha(!showSenha)} tabIndex={-1}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}>
                      {showSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {senha.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[1, 2, 3, 4].map(i => (
                          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= strength.level ? strength.color : '#e2e8f0', transition: 'background 0.3s' }} />
                        ))}
                      </div>
                      <p style={{ fontSize: '.75rem', color: strength.color, marginTop: 6, fontWeight: 600 }}>Nível da senha: {strength.label}</p>
                    </div>
                  )}
                </div>

                {/* Confirmar Senha */}
                <label style={{ width: 120, textAlign: 'left', fontSize: '0.78rem', fontWeight: 700, color: '#475569', flexShrink: 0, marginTop: 10 }}>Confirme a senha</label>
                <div>
                  <input type={showSenha ? 'text' : 'password'} name="confirmarSenha" autoComplete="new-password" value={senhaConfirm} onChange={e => setSenhaConfirm(e.target.value)}
                    placeholder="Repita a senha" style={{ ...inputStyle, borderColor: senhaConfirm && senha !== senhaConfirm ? '#ef4444' : '#e2e8f0' }} />
                  {senhaConfirm && senha !== senhaConfirm && <p style={{ color: '#ef4444', fontSize: '0.7rem', marginTop: 4, textAlign: 'left' }}>As senhas não coincidem</p>}
                </div>
              </div>

              <button type="submit" onClick={handleActivate} disabled={!senhaValida || loading}
                style={{
                  width: '100%', padding: '16px 24px',
                  background: senhaValida ? 'linear-gradient(135deg, #6c5ce7, #5a4fcf)' : '#f8fafc',
                  color: senhaValida ? 'white' : '#94a3b8',
                  border: senhaValida ? 'none' : '1px solid #e2e8f0',
                  borderRadius: 16, fontWeight: 800, fontSize: '1rem',
                  cursor: senhaValida ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  boxShadow: senhaValida ? '0 8px 30px rgba(108,92,231,0.4), inset 0 1px 0 rgba(255,255,255,0.2)' : 'none',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  marginTop: 8
                }}>
                {loading ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Preparando acesso...</> : 'Ativar Minha Conta'}
              </button>
            </div>
          </div>
        )}

        {/* Step: Tipo de Dispositivo (público/privado) */}
        {!error && step === DEVICE_STEP && (() => {
          const ua = navigator.userAgent;
          const navegador = /Chrome/.test(ua) ? 'Chrome' : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Outro';
          const sistemaOp = /Android/.test(ua) ? 'Android' : /iPhone|iPad|iPod/.test(ua) ? 'iOS' : /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Outro';
          const fp = [ua, screen.width, screen.height, screen.colorDepth, Intl.DateTimeFormat().resolvedOptions().timeZone].join('|');
          return (
            <DeviceRegistrationDialog
              fingerprint={fp}
              navegador={navegador}
              sistemaOp={sistemaOp}
              onComplete={(somenteMeu) => {
                setDeviceSomenteMeu(somenteMeu);
                if (somenteMeu) {
                  // Dispositivo privado → avançar para cadastro de Passkey
                  setStep(PASSKEY_STEP);
                } else {
                  // Dispositivo público → pular passkey, ir direto para Ativada
                  setStep(DONE_STEP);
                  setTimeout(() => {
                    const perfil = userInfo?.perfil;
                    if (perfil === 'PROFESSOR') navigate('/professor/dashboard');
                    else navigate('/login');
                  }, 2000);
                }
              }}
            />
          );
        })()}

        {/* Step: Cadastrar Chave de Acesso (PassKey) — só para dispositivo privado */}
        {!error && step === PASSKEY_STEP && (
          <div style={{ padding: '8px 0' }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 20,
              textAlign: 'left',
              marginBottom: 20,
              width: '100%'
            }}>
              {/* Coluna Esquerda: Ícone de digital */}
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid #ddd6fe', boxShadow: '0 0 0 4px rgba(124,58,237,0.06)',
                flexShrink: 0
              }}>
                <Fingerprint size={28} style={{ color: '#7c3aed' }} />
              </div>

              {/* Coluna Direita: Textos */}
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: 4, color: '#1e293b', letterSpacing: '-0.02em', lineHeight: 1.25 }}>
                  Cadastrar Chave de Acesso
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.8rem', margin: 0, lineHeight: 1.4 }}>
                  Sua conta foi ativada! Cadastre sua chave de seguranca para proteger seus acessos.
                </p>
              </div>
            </div>
            {passkeyRegError && (
              <div style={{ padding: '8px 12px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: '.76rem', fontWeight: 600, marginBottom: 12, textAlign: 'center' }}>
                {passkeyRegError}
              </div>
            )}
            <button onClick={handleRegisterPasskeyAfterActivation} disabled={passkeyRegLoading} style={{ width: '100%', padding: '13px 20px', background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)', color: 'white', border: 'none', borderRadius: 14, fontWeight: 700, fontSize: '.9rem', cursor: passkeyRegLoading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, boxShadow: '0 6px 24px rgba(108,92,231,0.35)', transition: 'all .3s', opacity: passkeyRegLoading ? 0.7 : 1 }}>
              {passkeyRegLoading ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Registrando...</> : <><Fingerprint size={16} /> Cadastrar Chave de Seguranca</>}
            </button>
          </div>
        )}

        {/* Step: ativada (fallback) */}
        {!error && step === DONE_STEP && (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', margin: '0 auto 14px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #bbf7d0', boxShadow: '0 0 0 4px rgba(22,163,106,0.06)' }}>
              <CheckCircle2 size={26} style={{ color: '#16a34a' }} />
            </div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: 6, color: '#16a34a' }}>Conta Ativada!</h2>
            <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: 10, lineHeight: 1.5 }}>
              Sua conta foi ativada com sucesso.
              {userInfo && <><br />Bem-vindo(a), <strong style={{ color: '#1e293b' }}>{userInfo.nome}</strong>!</>}
            </p>
            {userInfo?.email && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 18, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Seu login:</span>
                <span style={{ fontSize: '.84rem', fontWeight: 700, color: '#1e293b', fontFamily: 'monospace', letterSpacing: '.2px' }}>{maskEmail(userInfo.email)}</span>
              </div>
            )}
            <button onClick={() => navigate('/login')} style={{ width: '100%', padding: '13px 20px', background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: 'white', border: 'none', borderRadius: 14, fontWeight: 700, fontSize: '.9rem', cursor: 'pointer', boxShadow: '0 6px 24px rgba(22,163,106,0.3)', transition: 'all .3s' }}>
              Ir para Login
            </button>
          </div>
        )}
      </div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (min-width: 768px) {
          .password-step-card {
            max-width: 840px !important;
            text-align: left !important;
          }
          .ativar-grid {
            display: grid !important;
            grid-template-columns: 1fr 1.2fr !important;
            gap: 40px !important;
            align-items: center !important;
          }
          .ativar-left-col {
            border-right: 1px solid rgba(108,92,231,0.15) !important;
            padding-right: 40px !important;
            margin-bottom: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            height: 100% !important;
          }
          .ativar-right-col {
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
          }
        }
      `}</style>

      {/* Modal Diálogo Institucional Aluno Conectado — Reenvio de Convite */}
      <Modal
        open={showReenvioDialog}
        onClose={() => setShowReenvioDialog(false)}
        size="sm"
        hideCloseButton
      >
        <div style={{ textAlign: 'center', padding: '10px 8px 18px' }}>
          <div style={{
            width: 58,
            height: 58,
            borderRadius: '50%',
            margin: '0 auto 16px',
            background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 6px 16px rgba(22, 163, 74, 0.18)',
            border: '2px solid #86efac',
          }}>
            <Send size={26} style={{ color: '#16a34a' }} />
          </div>

          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 12px',
            borderRadius: 9999,
            background: '#ede9fe',
            color: '#7c3aed',
            fontSize: '0.72rem',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: 10,
          }}>
            <Shield size={11} /> Aluno Conectado Oficial
          </span>

          <h3 style={{
            fontSize: '1.15rem',
            fontWeight: 800,
            color: '#0f172a',
            marginBottom: 10,
            letterSpacing: '-0.02em',
          }}>
            Solicitação de Reenvio Registrada!
          </h3>

          <p style={{
            color: '#475569',
            fontSize: '0.85rem',
            lineHeight: 1.6,
            marginBottom: 22,
          }}>
            Enviamos um novo link de ativação válido com segurança para o <strong>WhatsApp</strong> e <strong>E-mail</strong> do responsável cadastrado na escola.
            <br /><br />
            Por favor, confira suas mensagens no WhatsApp e sua caixa de entrada de e-mail (incluindo a pasta de spam) para prosseguir com a ativação.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              onClick={() => {
                setShowReenvioDialog(false);
                navigate('/login');
              }}
              style={{
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: 12,
                fontSize: '0.88rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                transition: 'all 0.2s',
              }}
            >
              Entendido, ir para a Tela de Login
            </button>
            <button
              type="button"
              onClick={() => setShowReenvioDialog(false)}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                color: '#64748b',
                border: 'none',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Fechar
            </button>
          </div>
        </div>
      </Modal>

      {/* Lab de Testes — disponível na página de ativação para simulação do cadastro de senha */}
      <LabPublicBar autoOpen={new URLSearchParams(window.location.search).has('lab-active')} />
    </div>
  );
}
//#endregion AC_BLOCK ativar-page.ativar-page
